import { useEffect, type ReactNode } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { AlertCircle, CreditCard, IndianRupee, Info } from 'lucide-react';
import { MonthYearPicker } from '@/components/MonthYearPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { StatusBadge, TextBadge, paymentTypeStatus } from '@/components/StatusBadge';
import { SideDrawer } from '@/components/drawer/SideDrawer';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { FieldError } from '@/components/form/FieldError';
import { ALL_PAYMENT_TYPES, PaymentTypeField } from '@/components/form/PaymentTypeField';
import { PmsBalanceNote } from '@/components/form/PmsBalanceNote';
import { getApiErrorMessage } from '@/api/errorHandler';
import { PaymentAPIRequestMapper } from '@/api/mapper/payment';
import { createPayment } from '@/api/service/payment';
import { useRefreshPolicyData } from '@/api/query/refresh';
import { PAID_BY_PMS, type PaymentType } from '@/api/type/payment';
import type { Member, Policy } from '@/types';
import {
  MONTHLY_FEE,
  addMonthsToMonthYear,
  calculateDuePeriod,
  compareMonthYear,
  formatCurrency,
  formatMonthYearDisplay,
  formatMonthYearPayload,
  getCurrentMonthYear,
  type MonthYear,
} from '@/lib/payments';
import { dateToIso, todayDateDisplay } from '@/lib/dates';
import { currentMonthKey, formatShortMonth } from '@/lib/months';
import { canPayPolicy } from '@/lib/policies';
import { FIELD_CLASS, LABEL_CLASS, PRIMARY_BUTTON_CLASS } from '@/lib/styles';
import { displayMemberNo, SUCCESS_NOTICE_ACCENT_CLASS, SUCCESS_NOTICE_CLASS } from '@/lib/utils';

interface UpsertPaymentProps {
  member: Member | null;
  policy: Policy | null;
  /** The member's balance due, shown when Paid by PMS is picked. */
  balanceDue: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type PaymentFormValues = {
  amount: string;
  paymentType: PaymentType;
  dueStart: MonthYear;
  dueEnd: MonthYear;
};

const FORM_ID = 'policy-payment-form';

const ordinal = (value: number) => {
  const lastTwo = value % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${value}th`;
  return `${value}${{ 1: 'st', 2: 'nd', 3: 'rd' }[value % 10] ?? 'th'}`;
};

const initialValues = (): PaymentFormValues => ({
  amount: String(MONTHLY_FEE),
  paymentType: 'Cash',
  dueStart: getCurrentMonthYear(),
  dueEnd: getCurrentMonthYear(),
});

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-6 py-3.5">
      <dt className="shrink-0 text-sm text-gray-500">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-gray-900">{children}</dd>
    </div>
  );
}

/** Policy Payment drawer: adds a payment for one month or a range of months on a policy. */
export function UpsertPayment({ member, policy, balanceDue, open, onOpenChange }: UpsertPaymentProps) {
  const refreshPolicyData = useRefreshPolicyData();
  const canPay = policy ? canPayPolicy(policy) : false;
  const paidThisMonth = policy?.thisMonth === 'paid';

  const formik = useFormik<PaymentFormValues>({
    initialValues: initialValues(),
    validationSchema: yup.object({
      // A policy month is exactly ₹100, so the amount is ₹100, ₹200, ₹300…
      amount: yup
        .string()
        .trim()
        .required('Please enter amount')
        .test('amount-valid', 'Please enter a valid amount', (value) => Number(value) > 0)
        .test(
          'amount-multiple',
          `Amount must be in multiples of ₹${MONTHLY_FEE} (₹${MONTHLY_FEE}, ₹${MONTHLY_FEE * 2}, ₹${MONTHLY_FEE * 3}…)`,
          (value) => Number(value) % MONTHLY_FEE === 0
        ),
      paymentType: yup.mixed<PaymentType>().oneOf([...ALL_PAYMENT_TYPES]).required('Please select payment type'),
    }),
    validate: (values) => {
      if (compareMonthYear(values.dueEnd, values.dueStart) < 0) {
        return { dueEnd: 'End month cannot be before start month' };
      }
      // One payment of ₹100 per month: the amount must match the months chosen.
      const months = calculateDuePeriod(values.dueStart, values.dueEnd);
      const amount = Number(values.amount);
      if (amount > 0 && amount % MONTHLY_FEE === 0 && amount !== months * MONTHLY_FEE) {
        return {
          amount: `Amount must be ₹${months * MONTHLY_FEE} for ${months} ${months === 1 ? 'month' : 'months'} (₹${MONTHLY_FEE} per month)`,
        };
      }
      return {};
    },
    onSubmit: async (values, { setStatus }) => {
      if (!policy || !canPay) return;
      setStatus(undefined);
      try {
        await createPayment(
          PaymentAPIRequestMapper({
            policyId: policy.id,
            amount: Number(values.amount),
            paymentType: values.paymentType,
            paymentDate: dateToIso(todayDateDisplay()),
            duePeriod: calculateDuePeriod(values.dueStart, values.dueEnd),
            dueStart: formatMonthYearPayload(values.dueStart),
            dueEnd: formatMonthYearPayload(values.dueEnd),
          })
        );
        await refreshPolicyData();
        onOpenChange(false);
      } catch (error) {
        setStatus(getApiErrorMessage(error));
      }
    },
  });

  // Start every opening from the current month.
  useEffect(() => {
    if (open) formik.resetForm({ values: initialValues() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, policy?.id]);

  const showError = (name: keyof PaymentFormValues) =>
    Boolean((formik.touched[name] || formik.submitCount > 0) && formik.errors[name]);

  const duePeriod = calculateDuePeriod(formik.values.dueStart, formik.values.dueEnd);
  const dueNumber = Math.min(policy?.totalMonths ?? 0, (policy?.paidMonths ?? 0) + 1);
  const thisMonthName = formatShortMonth(currentMonthKey());

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && formik.isSubmitting) return;
    onOpenChange(nextOpen);
  };

  return (
    <SideDrawer
      open={open}
      onOpenChange={handleOpenChange}
      title="Policy Payment"
      member={member}
      tag={policy?.policyNo}
      footer={
        <>
          {formik.status && (
            <div className="mr-auto flex min-w-0 flex-1 items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{formik.status}</span>
            </div>
          )}
          <Button type="button" variant="outline" className="border-gray-200" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={!canPay || formik.isSubmitting}
            className={`min-w-36 ${PRIMARY_BUTTON_CLASS}`}
          >
            {formik.isSubmitting ? 'Saving...' : 'Add Policy Payment'}
          </Button>
        </>
      }
    >
      {member && policy && (
        <form id={FORM_ID} noValidate onSubmit={formik.handleSubmit} className="space-y-5">
          {!canPay && (
            <div className={SUCCESS_NOTICE_CLASS}>
              {policy.status === 'closed' ? (
                'This policy is closed. Payments are not allowed.'
              ) : (
                <>
                  All <span className={SUCCESS_NOTICE_ACCENT_CLASS}>{policy.totalMonths} months</span> are paid.{' '}
                  <span className={SUCCESS_NOTICE_ACCENT_CLASS}>No dues</span> remaining — payments cannot be added.
                </>
              )}
            </div>
          )}

          {canPay && (
            <DrawerSection
              icon={IndianRupee}
              title="Policy Payment Details"
              description="Select the due period (months) and payment type."
              aside={
                <TextBadge tone={paidThisMonth ? 'teal' : 'amber'}>
                  {`Policy ${paidThisMonth ? 'Paid' : 'Due'} for ${thisMonthName}`}
                </TextBadge>
              }
            >

              <div className="space-y-2">
                <div className="flex min-h-5 items-center gap-2">
                  <Label htmlFor={`${FORM_ID}-due-period`} className={LABEL_CLASS}>
                    Due Period *
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="inline-flex items-center text-gray-400 hover:text-gray-600">
                        <Info className="h-4 w-4" />
                        <span className="sr-only">Due period info</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Select the starting and ending month for this payment.</TooltipContent>
                  </Tooltip>
                </div>
                <MonthYearPicker
                  id={`${FORM_ID}-due-period`}
                  startMonth={formik.values.dueStart}
                  endMonth={formik.values.dueEnd}
                  onChange={(start, end) => {
                    // One update, so validation sees the new months and amount together.
                    formik.setValues({
                      ...formik.values,
                      dueStart: start,
                      dueEnd: end,
                      amount: String(calculateDuePeriod(start, end) * MONTHLY_FEE),
                    });
                  }}
                />
                <FieldError show={Boolean(formik.errors.dueEnd)} message={formik.errors.dueEnd as string} />
              </div>

              <div className="space-y-2">
                <div className="flex min-h-5 items-center justify-between">
                  <Label htmlFor={`${FORM_ID}-amount`} className={LABEL_CLASS}>
                    Amount (₹) *
                  </Label>
                  <span className="text-xs text-gray-400">₹{MONTHLY_FEE} / month</span>
                </div>
                <Input
                  id={`${FORM_ID}-amount`}
                  name="amount"
                  inputMode="numeric"
                  value={formik.values.amount}
                  onChange={(e) => {
                    // Whole rupees only; show the amount error while typing.
                    const value = e.target.value.replace(/\D/g, '');
                    // Typing an amount moves the end month to match it.
                    const months = Math.floor(Number(value) / MONTHLY_FEE);
                    const dueEnd =
                      months >= 1 && months <= policy.totalMonths
                        ? addMonthsToMonthYear(formik.values.dueStart, months - 1)
                        : formik.values.dueEnd;
                    // One update, so validation sees the new amount and end month together.
                    formik.setFieldTouched('amount', true, false);
                    formik.setValues({ ...formik.values, amount: value, dueEnd });
                  }}
                  onBlur={formik.handleBlur}
                  placeholder={String(MONTHLY_FEE)}
                  className={FIELD_CLASS}
                />
                <FieldError show={showError('amount')} message={formik.errors.amount} />
              </div>

              <PaymentTypeField
                id={FORM_ID}
                options={ALL_PAYMENT_TYPES}
                value={formik.values.paymentType}
                onChange={(value) => formik.setFieldValue('paymentType', value)}
              />
              {formik.values.paymentType === PAID_BY_PMS && (
                <PmsBalanceNote
                  memberName={member.name}
                  amount={Number(formik.values.amount) > 0 ? Number(formik.values.amount) : null}
                  currentBalance={balanceDue}
                />
              )}
            </DrawerSection>
          )}

          {canPay && (
            <DrawerSection icon={CreditCard} title="Summary" description="Review this payment before saving." padded={false}>
              <dl className="divide-y divide-gray-100 px-5 py-2">
                <SummaryRow label="Member">
                  <span className="block truncate font-medium">
                    <span className="tabular-nums text-gray-400">#{displayMemberNo(member)}</span>
                    <span className="mx-1.5 text-gray-300">·</span>
                    {member.name}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">{member.phone}</span>
                </SummaryRow>
                <SummaryRow label="Policy">
                  <span className="font-medium">{policy.policyNo}</span>
                </SummaryRow>
                <SummaryRow label="Due Period">
                  <span className="block font-medium">
                    {duePeriod} {duePeriod === 1 ? 'month' : 'months'} ({ordinal(dueNumber)} due)
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    {formatMonthYearDisplay(formik.values.dueStart)}
                    {duePeriod > 1 && ` – ${formatMonthYearDisplay(formik.values.dueEnd)}`}
                  </span>
                </SummaryRow>
                <SummaryRow label="Payment Date">
                  <span className="font-medium tabular-nums">{todayDateDisplay()}</span>
                  <span className="mt-0.5 block text-[11px] text-gray-400">(Today)</span>
                </SummaryRow>
                <SummaryRow label="Amount">
                  <span className="font-semibold tabular-nums">{formatCurrency(Number(formik.values.amount) || 0)}</span>
                </SummaryRow>
                <SummaryRow label="Payment Type">
                  <StatusBadge status={paymentTypeStatus(formik.values.paymentType)} />
                </SummaryRow>
              </dl>
            </DrawerSection>
          )}
        </form>
      )}
    </SideDrawer>
  );
}
